import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import PaymentButtons from "@/components/PaymentButtons";

export default function AjoPage() {
  const [people, setPeople] = useState(6);
  const [amount, setAmount] = useState(100);
  const [frequency, setFrequency] = useState("monthly");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [memberNames, setMemberNames] = useState("");
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchHistory = async () => {
    try {
      const { data } = await api.get("/ajo/plans");
      setHistory(data);
    } catch (err) {
      console.error("Failed to load ajo history:", err);
    }
  };
  useEffect(() => { fetchHistory(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPlan(null);
    try {
      const names = memberNames.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/ajo/plan", {
        people: Number(people),
        amount: Number(amount),
        frequency,
        start_date: startDate,
        member_names: names.length ? names : null,
      });
      setPlan(data);
      toast.success("Ajo plan generated & saved");
      fetchHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate plan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Ajo Planner</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">Plan your rotating savings group</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">Enter the group size, contribution and frequency. We'll generate a fair payout schedule you can save and share.</p>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <form onSubmit={submit} className="space-y-6 rounded-2xl border border-border bg-card p-6 md:p-8" data-testid="ajo-form">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="people">Number of people</Label>
              <Input id="people" type="number" min={2} max={100} value={people} onChange={(e) => setPeople(e.target.value)} className="h-11" data-testid="ajo-people-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="amount">Contribution per person</Label>
              <Input id="amount" type="number" min={1} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11" data-testid="ajo-amount-input" />
            </div>
            <div className="space-y-2">
              <Label>Frequency</Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger className="h-11" data-testid="ajo-frequency-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="start">Start date</Label>
              <Input id="start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-11" data-testid="ajo-start-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="names">Member names (optional, comma separated)</Label>
            <Input id="names" placeholder="Ada, Tunde, Bisi…" value={memberNames} onChange={(e) => setMemberNames(e.target.value)} className="h-11" data-testid="ajo-names-input" />
          </div>
          <Button type="submit" className="h-11 w-full" disabled={loading} data-testid="ajo-submit-btn">
            {loading ? "Generating…" : "Generate plan"}
          </Button>
        </form>

        <div className="rounded-2xl border border-border bg-card p-6 md:p-8" data-testid="ajo-result">
          {plan ? (
            <>
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Pot per cycle</p>
                  <p className="font-display text-3xl">£{plan.pot_per_cycle.toFixed(2)}</p>
                </div>
                <div className="text-sm text-muted-foreground">
                  {plan.people} members · {plan.frequency} · starts {plan.start_date}
                </div>
              </div>
              <div className="mt-6 divide-y divide-border rounded-xl border border-border">
                {plan.schedule.map((row) => (
                  <div key={row.position} className="flex items-center justify-between px-4 py-3 text-sm">
                    <div className="flex items-center gap-3">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">{row.position}</span>
                      <span>{row.member}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">£{row.payout_amount.toFixed(2)}</div>
                      <div className="text-xs text-muted-foreground">{row.payout_date}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 space-y-3">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Pay your contribution</p>
                <PaymentButtons />
              </div>
            </>
          ) : (
            <div className="flex h-full min-h-[280px] flex-col items-center justify-center text-center text-muted-foreground">
              <p>Fill in the form to generate a fair payout schedule.</p>
            </div>
          )}
        </div>
      </div>

      {history.length > 0 && (
        <div className="mt-12">
          <h2 className="font-display text-xl">Your recent plans</h2>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {history.slice(0, 6).map((p) => (
              <button
                key={p.plan_id}
                onClick={() => setPlan(p)}
                className="rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/60"
                data-testid={`ajo-history-${p.plan_id}`}
              >
                <div className="text-sm font-medium">{p.people} members · £{p.amount}</div>
                <div className="mt-1 text-xs text-muted-foreground">{p.frequency} · starts {p.start_date}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
